import {
  Component,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ErrorInfo,
  type FormEvent,
  type ReactNode,
} from "react";

import type {
  BreakdownRow,
  ChargeSort,
  CostDimension,
  CostFilters,
  DashboardResponse,
} from "../shared/contracts";
import type { AuthenticationStep, DashboardApi, SavedView } from "./api";
import iconUrl from "./assets/Watchpoint_Icon.png";
import logoUrl from "./assets/Watchpoint_Logo.png";
import { AskMode } from "./components/AskMode";
import { Assistant, type ChatTurn } from "./components/Assistant";
import {
  readStoredConversations,
  summarizeConversations,
  upsertConversation,
  writeStoredConversations,
  type StoredConversation,
} from "./conversation-store";
import {
  ChargesCard,
  chargesCsv,
  ChargesTable,
} from "./components/ChargesCard";
import {
  dimensions,
  DriversCard,
  driversCsv,
  DriversTable,
} from "./components/DriversCard";
import {
  Sidebar,
  readSidebarPref,
  storeSidebarPref,
  useRailForced,
  type SidebarMode,
} from "./components/Sidebar";
import { TableDialog } from "./components/TableDialog";
import { downloadCsv } from "./csv";

const defaultSort: ChargeSort = { key: "cost", direction: "desc" };

function iso(date: Date): string {
  return date.toISOString().slice(0, 10);
}

export function defaultFilters(now = new Date()): CostFilters {
  return {
    start: iso(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1))),
    end: iso(
      new Date(
        Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1),
      ),
    ),
    granularity: "day",
    services: [],
    linkedAccounts: [],
    regions: [],
    usageTypes: [],
    tagValues: [],
    includeCredits: true,
  };
}

function filtersFromUrl(): CostFilters {
  const fallback = defaultFilters();
  const query = new URLSearchParams(window.location.search);
  return {
    ...fallback,
    start: query.get("start") ?? fallback.start,
    end: query.get("end") ?? fallback.end,
  };
}

/** "Share filters" writes groupBy into the link, so a shared link must restore it. */
export function dimensionFromUrl(
  search = window.location.search,
): CostDimension {
  const requested = new URLSearchParams(search).get("groupBy");
  return dimensions.some((dimension) => dimension.value === requested)
    ? (requested as CostDimension)
    : "service";
}

export function currency(value: number, code = "USD"): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: code,
    maximumFractionDigits: 2,
  }).format(value);
}

export function signed(value: number, code: string): string {
  return `${value > 0 ? "+" : value < 0 ? "−" : ""}${currency(Math.abs(value), code)}`;
}

export function percent(value: number | null): string {
  if (value === null) return "new";
  return `${value >= 0 ? "+" : "−"}${Math.abs(value).toFixed(1)}%`;
}

export function EmptyRow({ span, message }: { span: number; message: string }) {
  return (
    <tr>
      <td className="muted" colSpan={span}>
        {message}
      </td>
    </tr>
  );
}

function DataNotice({ state }: { state: DashboardResponse["dataState"] }) {
  if (state === "ready") return null;
  return (
    <div className="state-card" role="status">
      <div>
        <strong>
          {state === "pending"
            ? "Waiting on the first Cost and Usage Report delivery"
            : "No charges match these filters"}
        </strong>
        <p>
          {state === "pending"
            ? "The Athena table and its partitions resolved, so the pipeline is healthy — AWS has not written CUR rows for this range yet. Data Exports refreshes at least daily; widen the range or check back later."
            : "Every query ran successfully but the active filters exclude all line items. Broaden the date range or clear a filter."}
        </p>
      </div>
    </div>
  );
}

function MetricCard({
  label,
  value,
  detail,
  status,
  textual,
  ask,
}: {
  label: string;
  value: string;
  detail: string;
  status?: "success" | "warning";
  textual?: boolean;
  ask?: string;
}) {
  return (
    <article className="metric-card" data-ask={ask}>
      <p className="metric-card__label">{label}</p>
      <p
        className={
          textual
            ? "metric-card__value metric-card__value--text"
            : "metric-card__value"
        }
      >
        {value}
      </p>
      <p
        className={
          status
            ? `metric-card__detail metric-card__detail--${status}`
            : "metric-card__detail"
        }
      >
        {detail}
      </p>
    </article>
  );
}

function MetricsGrid({
  data,
  change,
  topDriver,
}: {
  data: DashboardResponse;
  change: number | null | undefined;
  topDriver: BreakdownRow | undefined;
}) {
  const monthToDateValue = currency(data.summary.monthToDate, data.currency);
  const monthToDateDetail = `${change === null || change === undefined ? "No comparison" : `${change >= 0 ? "+" : ""}${change.toFixed(1)}%`} vs previous equivalent`;
  const amortizedValue = currency(
    data.summary.amortizedMonthToDate,
    data.currency,
  );
  const amortizedDetail =
    Math.abs(data.summary.amortizedMonthToDate - data.summary.monthToDate) <
    0.005
      ? "Matches unblended · no commitment coverage"
      : `${signed(data.summary.amortizedMonthToDate - data.summary.monthToDate, data.currency)} vs unblended`;
  const projectedValue =
    data.summary.projectedMonth === null
      ? "Not available"
      : currency(data.summary.projectedMonth, data.currency);
  const projectedDetail =
    data.summary.projectionBasisDays === 0
      ? "Needs a full completed day from the 1st"
      : `Run rate from ${data.summary.projectionBasisDays} complete day${data.summary.projectionBasisDays === 1 ? "" : "s"} · not an AWS forecast`;
  const largestDriverValue = topDriver?.label ?? "No data";
  const largestDriverDetail = topDriver
    ? `${currency(topDriver.cost, data.currency)} · ${(topDriver.share * 100).toFixed(1)}% of spend`
    : "No driver detected";

  return (
    <section className="metrics-grid" aria-label="Cost summary">
      <MetricCard
        label="Month to date"
        value={monthToDateValue}
        detail={monthToDateDetail}
        status={
          change !== null && change !== undefined && change > 10
            ? "warning"
            : "success"
        }
        ask={`Metric — Month to date: ${monthToDateValue}, ${monthToDateDetail}`}
      />
      <MetricCard
        label="Amortized to date"
        value={amortizedValue}
        detail={amortizedDetail}
        ask={`Metric — Amortized to date: ${amortizedValue}, ${amortizedDetail}`}
      />
      <MetricCard
        label="Projected month"
        value={projectedValue}
        detail={projectedDetail}
        ask={`Metric — Projected month: ${projectedValue}, ${projectedDetail}`}
      />
      <MetricCard
        label="Largest driver"
        value={largestDriverValue}
        detail={largestDriverDetail}
        ask={`Metric — Largest driver: ${largestDriverValue}, ${largestDriverDetail}`}
        textual
      />
    </section>
  );
}

function TrendChart({
  data,
  code,
}: {
  data: DashboardResponse["trend"];
  code: string;
}) {
  const width = 720;
  const height = 220;
  const padding = 20;
  const max = Math.max(...data.map((point) => point.cost), 1);
  const points = data
    .map((point, index) => {
      const x =
        padding +
        (index / Math.max(data.length - 1, 1)) * (width - padding * 2);
      const y = height - padding - (point.cost / max) * (height - padding * 2);
      return `${x},${y}`;
    })
    .join(" ");

  return (
    <div className="trend-chart">
      <svg
        className="trend-chart__visual"
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label="Daily cost trend"
        aria-describedby="trend-description"
      >
        <title id="trend-title">Daily cost trend</title>
        <desc id="trend-description">
          {data.length === 0
            ? "No cost points are available."
            : `Costs from ${data[0]?.date} to ${data.at(-1)?.date}. Peak cost is ${currency(max, code)}.`}
        </desc>
        <line
          className="trend-chart__axis"
          x1={padding}
          y1={height - padding}
          x2={width - padding}
          y2={height - padding}
        />
        <polyline className="trend-chart__line" points={points} />
      </svg>
      <table className="sr-only">
        <caption>Accessible daily cost values</caption>
        <thead>
          <tr>
            <th>Date</th>
            <th>Cost</th>
          </tr>
        </thead>
        <tbody>
          {data.map((point) => (
            <tr key={point.date}>
              <td>{point.date}</td>
              <td>{currency(point.cost, code)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="state-card" role="status" aria-live="polite">
      <span className="spinner" aria-hidden="true" />
      <div>
        <strong>Loading cost data</strong>
        <p>Athena may take a few seconds on a cold query.</p>
      </div>
    </div>
  );
}

/**
 * Branded splash shown while the Cognito session is being restored. The
 * lifecycle is driven purely by animation events: the icon's entrance
 * (zip in from the left, twirl, pop) doubles as the minimum display time,
 * and once it finishes the icon zips out to the right while the overlay
 * fades and unmounts. jsdom fires no animation events, so tests see the
 * overlay sit inertly above the app without hiding it from queries.
 */
function Splash({
  exiting,
  onEntered,
  onExited,
}: {
  exiting: boolean;
  onEntered: () => void;
  onExited: () => void;
}) {
  return (
    <div
      className={exiting ? "splash splash--out" : "splash"}
      role="status"
      aria-label="Loading Watchpoint"
      onAnimationEnd={(event) => {
        if (event.target === event.currentTarget) onExited();
      }}
    >
      <img
        className="splash__icon"
        src={iconUrl}
        alt=""
        onAnimationEnd={onEntered}
      />
    </div>
  );
}

function Login({
  api,
  onSuccess,
}: {
  api: DashboardApi;
  onSuccess: () => void;
}) {
  const [step, setStep] = useState<
    "signIn" | "newPasswordRequired" | "mfaRequired"
  >("signIn");
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);
  const advance = useCallback(
    (next: AuthenticationStep) => {
      if (next === "authenticated") onSuccess();
      else setStep(next);
    },
    [onSuccess],
  );
  const submit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setPending(true);
      setError("");
      const data = new FormData(event.currentTarget);
      try {
        if (step === "signIn") {
          const email = data.get("email");
          const password = data.get("password");
          advance(
            await api.login(
              typeof email === "string" ? email : "",
              typeof password === "string" ? password : "",
            ),
          );
        } else if (step === "newPasswordRequired") {
          const password = data.get("newPassword");
          const confirmation = data.get("confirmPassword");
          if (
            typeof password !== "string" ||
            password !== confirmation ||
            password.length < 14 ||
            !/[a-z]/.test(password) ||
            !/[A-Z]/.test(password) ||
            !/[0-9]/.test(password) ||
            !/[^A-Za-z0-9]/.test(password)
          ) {
            setError("Passwords must match and meet all listed requirements.");
            return;
          }
          advance(await api.completeNewPassword(password));
        } else {
          const code = data.get("code");
          advance(await api.completeMfa(typeof code === "string" ? code : ""));
        }
      } catch {
        setError(
          step === "signIn"
            ? "Sign-in failed. Check your credentials or ask an administrator for access."
            : "The authentication challenge failed. Check the value and try again.",
        );
      } finally {
        setPending(false);
      }
    },
    [advance, api, step],
  );

  const title =
    step === "signIn"
      ? "Sign in to cost analytics"
      : step === "newPasswordRequired"
        ? "Choose a permanent password"
        : "Enter your verification code";

  return (
    <main className="auth-shell">
      <section className="auth-card" aria-labelledby="sign-in-title">
        <img className="auth-card__logo" src={logoUrl} alt="Watchpoint" />
        <h1 id="sign-in-title">{title}</h1>
        <p>
          {step === "signIn"
            ? "Use the Cognito account provided by your administrator."
            : step === "newPasswordRequired"
              ? "Your temporary password must be replaced before first sign-in."
              : "Enter the six-digit code from your authenticator."}
        </p>
        <form
          key={step}
          className="form-stack"
          onSubmit={(event) => {
            void submit(event);
          }}
        >
          {step === "signIn" ? (
            <>
              <label>
                Email
                <input
                  name="email"
                  type="email"
                  autoComplete="username"
                  required
                />
              </label>
              <label>
                Password
                <input
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                />
              </label>
            </>
          ) : step === "newPasswordRequired" ? (
            <>
              <label>
                New password
                <input
                  name="newPassword"
                  type="password"
                  autoComplete="new-password"
                  minLength={14}
                  required
                />
              </label>
              <label>
                Confirm new password
                <input
                  name="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  minLength={14}
                  required
                />
              </label>
              <p className="muted">
                Use at least 14 characters with uppercase, lowercase, a number,
                and a symbol.
              </p>
            </>
          ) : (
            <label>
              Verification code
              <input
                name="code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                maxLength={6}
                required
              />
            </label>
          )}
          <button
            className="button button--primary"
            disabled={pending}
            type="submit"
          >
            {pending
              ? "Verifying…"
              : step === "signIn"
                ? "Sign in"
                : step === "newPasswordRequired"
                  ? "Set password"
                  : "Verify code"}
          </button>
          <p
            className="inline-message inline-message--error"
            aria-live="polite"
          >
            {error}
          </p>
        </form>
      </section>
    </main>
  );
}

export function App({ api }: { api: DashboardApi }) {
  const [authenticated, setAuthenticated] = useState(false);
  const [authReady, setAuthReady] = useState(false);
  const [splashEntered, setSplashEntered] = useState(false);
  const [splashDone, setSplashDone] = useState(false);
  const [filters, setFilters] = useState(filtersFromUrl);
  const [groupBy, setGroupBy] = useState<CostDimension>(dimensionFromUrl);
  const [data, setData] = useState<DashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [chatOpen, setChatOpen] = useState(false);
  const [unread, setUnread] = useState(false);
  const chatOpenRef = useRef(chatOpen);
  const [askMode, setAskMode] = useState(false);
  const [askContext, setAskContext] = useState<string | null>(null);
  // Threads persist in the browser so an answer survives a reload and older
  // conversations stay reachable from the panel's history view.
  const [conversations, setConversations] = useState<StoredConversation[]>(
    readStoredConversations,
  );
  const [turns, setTurns] = useState<ChatTurn[]>(
    () => conversations[0]?.turns ?? [],
  );
  const [chatPending, setChatPending] = useState(false);
  // The conversation id is also the AgentCore runtime session id, so it must be
  // stable for the whole thread and a fresh UUID when the user starts over.
  const [conversationId, setConversationId] = useState(
    () => conversations[0]?.id ?? crypto.randomUUID(),
  );
  const [views, setViews] = useState<SavedView[]>([]);
  const [sort, setSort] = useState<ChargeSort>(defaultSort);
  const [expandedTable, setExpandedTable] = useState<
    "drivers" | "charges" | null
  >(null);
  const [sidebarPref, setSidebarPref] = useState<SidebarMode>(readSidebarPref);
  const railForced = useRailForced();
  const sidebarMode: SidebarMode = railForced ? "rail" : sidebarPref;
  const toggleSidebar = useCallback(() => {
    setSidebarPref((current) => {
      const next = current === "rail" ? "expanded" : "rail";
      storeSidebarPref(next);
      return next;
    });
  }, []);

  const load = useCallback(async () => {
    if (!authenticated) return;
    setLoading(true);
    setError("");
    try {
      const [dashboard, savedViews] = await Promise.all([
        api.dashboard(filters, groupBy, sort),
        api.listViews(),
      ]);
      setData(dashboard);
      setViews(savedViews);
    } catch {
      setError(
        "Cost data could not be loaded. Verify Athena access and try again.",
      );
    } finally {
      setLoading(false);
    }
  }, [api, authenticated, filters, groupBy, sort]);

  useEffect(() => {
    let active = true;
    void api.prepare().then((ready) => {
      if (active) {
        setAuthenticated(ready);
        setAuthReady(true);
      }
    });
    return () => {
      active = false;
    };
  }, [api]);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      void load();
    }, 0);
    return () => window.clearTimeout(timeout);
  }, [load]);

  useEffect(() => {
    const query = new URLSearchParams({
      start: filters.start,
      end: filters.end,
      groupBy,
    });
    window.history.replaceState(null, "", `?${query.toString()}`);
  }, [filters, groupBy]);

  const changeFilter = useCallback((key: "start" | "end", value: string) => {
    setFilters((current) => ({ ...current, [key]: value }));
  }, []);
  const retry = useCallback(() => {
    void load();
  }, [load]);
  const sortCharges = useCallback((key: ChargeSort["key"]) => {
    setSort((current) =>
      current.key === key
        ? { key, direction: current.direction === "desc" ? "asc" : "desc" }
        : { key, direction: "desc" },
    );
  }, []);
  const exportDrivers = useCallback(() => {
    const group = data?.drivers.find((item) => item.dimension === groupBy);
    if (group) {
      downloadCsv(
        `watchpoint-drivers-${group.dimension}-${filters.start}-to-${filters.end}.csv`,
        driversCsv(group),
      );
    }
  }, [data, filters.end, filters.start, groupBy]);
  const exportCharges = useCallback(() => {
    if (data) {
      downloadCsv(
        `watchpoint-charges-${filters.start}-to-${filters.end}.csv`,
        chargesCsv(data.charges),
      );
    }
  }, [data, filters.end, filters.start]);
  // The FAB's unread dot only means something while the panel is closed, so
  // opening it always clears the flag. The ref lets submitQuestion see the
  // live open state from inside an async callback without depending on it.
  // Every path that changes chatOpen (toggling, entering ask mode, landing a
  // pick from ask mode) routes through here so the ref can never drift from
  // the state it mirrors.
  const setPanelOpen = useCallback((next: boolean) => {
    setChatOpen(next);
    chatOpenRef.current = next;
    if (next) setUnread(false);
  }, []);
  const toggleChat = useCallback(() => {
    // Ask mode owns the page while it's active; the FAB must not sneak the
    // panel back open underneath the selection banner.
    if (askMode) return;
    setPanelOpen(!chatOpen);
  }, [askMode, chatOpen, setPanelOpen]);
  // Esc-cancel should restore the panel only if entering ask mode is what
  // hid it — a mode entered from the closed-panel FAB should stay closed.
  const panelWasOpenRef = useRef(false);
  const enterAskMode = useCallback(() => {
    panelWasOpenRef.current = chatOpen;
    setPanelOpen(false);
    setAskMode(true);
  }, [chatOpen, setPanelOpen]);
  const pickAskTarget = useCallback(
    (context: string) => {
      setAskContext(context);
      setAskMode(false);
      setPanelOpen(true);
    },
    [setPanelOpen],
  );
  const cancelAskMode = useCallback(() => {
    setAskMode(false);
    if (panelWasOpenRef.current) setPanelOpen(true);
  }, [setPanelOpen]);
  const signedIn = useCallback(() => setAuthenticated(true), []);
  const splashIn = useCallback(() => setSplashEntered(true), []);
  const splashOut = useCallback(() => setSplashDone(true), []);

  const saveView = useCallback(async () => {
    const name = window.prompt("Name this view");
    if (!name) return;
    const view = await api.saveView(name, filters, groupBy);
    setViews((current) => [...current, view]);
  }, [api, filters, groupBy]);

  const share = useCallback(async () => {
    await navigator.clipboard.writeText(window.location.href);
  }, []);

  const submitQuestion = useCallback(
    async (message: string) => {
      if (!message || chatPending) return;
      setChatPending(true);
      const outgoing = askContext
        ? `[Context: ${askContext}]\n\n${message}`
        : message;
      setAskContext(null);
      // The chatPending guard means no other ask can interleave, so the next
      // turn list is known here — which lets the completed thread fold into
      // persistent history at the moment the answer (or failure) lands,
      // rather than through an effect watching state.
      const asked = [
        ...turns,
        { question: message, context: askContext ?? undefined },
      ];
      setTurns(asked);
      const complete = (finished: ChatTurn[]) => {
        setTurns(finished);
        setConversations((current) =>
          upsertConversation(
            current,
            conversationId,
            finished,
            new Date().toISOString(),
          ),
        );
      };
      try {
        const response = await api.assistant(outgoing, filters, conversationId);
        complete(
          asked.map((turn, index) =>
            index === asked.length - 1 ? { ...turn, response } : turn,
          ),
        );
      } catch {
        complete(
          asked.map((turn, index) =>
            index === asked.length - 1
              ? {
                  ...turn,
                  failure:
                    "The assistant is unavailable. Dashboard queries remain usable.",
                }
              : turn,
          ),
        );
      } finally {
        setChatPending(false);
        if (!chatOpenRef.current) setUnread(true);
      }
    },
    [api, filters, conversationId, chatPending, askContext, turns],
  );

  const startNewConversation = useCallback(() => {
    setConversationId(crypto.randomUUID());
    setTurns([]);
  }, []);

  useEffect(() => {
    writeStoredConversations(conversations);
  }, [conversations]);

  const conversationSummaries = useMemo(
    () => summarizeConversations(conversations),
    [conversations],
  );

  const openConversation = useCallback(
    (id: string) => {
      if (chatPending) return;
      const found = conversations.find(
        (conversation) => conversation.id === id,
      );
      if (!found) return;
      setConversationId(found.id);
      setTurns(found.turns);
    },
    [chatPending, conversations],
  );

  const deleteConversation = useCallback(
    (id: string) => {
      if (chatPending) return;
      setConversations((current) =>
        current.filter((conversation) => conversation.id !== id),
      );
      if (id === conversationId) {
        setConversationId(crypto.randomUUID());
        setTurns([]);
      }
    },
    [chatPending, conversationId],
  );

  const change = data?.summary.changePercent;
  const selectedGroup = useMemo(
    () => data?.drivers.find((group) => group.dimension === groupBy),
    [data, groupBy],
  );
  const topDriver = useMemo(
    () => [...(selectedGroup?.rows ?? [])].sort((a, b) => b.cost - a.cost)[0],
    [selectedGroup],
  );
  const trendPeak = useMemo(
    () => Math.max(0, ...(data?.trend.map((point) => point.cost) ?? [])),
    [data],
  );

  const splash = splashDone ? null : (
    <Splash
      exiting={splashEntered && authReady}
      onEntered={splashIn}
      onExited={splashOut}
    />
  );
  if (!authReady) return splash;
  if (!authenticated)
    return (
      <>
        {splash}
        <Login api={api} onSuccess={signedIn} />
      </>
    );

  const shellClasses = [
    "app-shell",
    sidebarMode === "rail" ? "app-shell--rail" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={shellClasses}>
      {splash}
      <Sidebar mode={sidebarMode} views={views} onToggle={toggleSidebar} />

      <main className="workspace" id="overview">
        <header className="page-header">
          <div>
            <p className="eyebrow">AWS cost intelligence</p>
            <h1>Cost overview</h1>
            <p>Traceable CUR 2.0 analytics for the active billing period.</p>
          </div>
          <div className="page-header__actions">
            <button
              className="button button--ghost"
              onClick={() => {
                void saveView();
              }}
              type="button"
            >
              Save view
            </button>
            <button
              className="button button--ghost"
              onClick={() => {
                void share();
              }}
              type="button"
            >
              Share filters
            </button>
          </div>
        </header>

        <section className="filter-bar" aria-label="Dashboard filters">
          <label>
            From
            <input
              type="date"
              value={filters.start}
              onChange={(event) => changeFilter("start", event.target.value)}
            />
          </label>
          <label>
            To
            <input
              type="date"
              value={filters.end}
              onChange={(event) => changeFilter("end", event.target.value)}
            />
          </label>
          <span className="filter-chip">Credits included</span>
          <span className="filter-bar__status" aria-live="polite">
            <span className="status-dot" />
            {loading && data
              ? "Refreshing…"
              : data
                ? `Updated ${new Date(data.freshness).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
                : "Waiting for data"}
          </span>
        </section>

        {/* Keep the rendered dashboard during a refetch. Re-sorting or changing a
            dimension re-queries Athena, and unmounting everything for a few seconds
            threw the reader back to the top of the page each time. */}
        {loading && !data ? (
          <LoadingState />
        ) : error ? (
          <div className="state-card state-card--error" role="alert">
            <div>
              <strong>Dashboard unavailable</strong>
              <p>{error}</p>
            </div>
            <button className="button button--ghost" onClick={retry}>
              Try again
            </button>
          </div>
        ) : !data ? (
          <LoadingState />
        ) : (
          <>
            <DataNotice state={data.dataState} />
            <MetricsGrid data={data} change={change} topDriver={topDriver} />

            {data.anomalies.length > 0 && (
              <section className="card" aria-labelledby="anomalies-heading">
                <div
                  className="card__header"
                  data-ask={`Anomalies section — ${String(data.anomalies.length)} rows shown`}
                >
                  <div>
                    <h2 id="anomalies-heading">Anomalies</h2>
                    <p>
                      Days whose spend broke from the trailing seven-day
                      baseline.
                    </p>
                  </div>
                  <span className="badge badge--pending">
                    {data.anomalies.length} flagged
                  </span>
                </div>
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Cost</th>
                        <th>Score</th>
                        <th>Why</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.anomalies.map((anomaly) => (
                        <tr
                          key={anomaly.date}
                          data-ask={`Anomaly — ${anomaly.date}: ${currency(anomaly.cost, data.currency)}, score ${anomaly.score.toFixed(2)}σ, ${anomaly.explanation}`}
                        >
                          <td className="number">{anomaly.date}</td>
                          <td className="number">
                            {currency(anomaly.cost, data.currency)}
                          </td>
                          <td className="number change-up">
                            {anomaly.score.toFixed(2)}σ
                          </td>
                          <td>{anomaly.explanation}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            <section
              className="card"
              aria-labelledby="trend-heading"
              data-ask={`Cost trend — daily unblended cost from ${filters.start} to ${filters.end}, peak ${currency(trendPeak, data.currency)}, ${String(data.trend.length)} points`}
            >
              <div
                className="card__header"
                data-ask={`Cost trend section — ${String(data.trend.length)} rows shown`}
              >
                <div>
                  <h2 id="trend-heading">Cost trend</h2>
                  <p>Daily unblended cost across active filters.</p>
                </div>
                <span className="badge badge--pending">CUR 2.0</span>
              </div>
              <TrendChart data={data.trend} code={data.currency} />
            </section>

            <DriversCard
              data={data}
              filters={filters}
              groupBy={groupBy}
              onSelectDimension={setGroupBy}
              onExport={exportDrivers}
              onExpand={() => setExpandedTable("drivers")}
            />

            <ChargesCard
              data={data}
              sort={sort}
              onSort={sortCharges}
              onExport={exportCharges}
              onExpand={() => setExpandedTable("charges")}
            />

            <TableDialog
              open={expandedTable === "drivers"}
              title="Cost drivers — all columns"
              onClose={() => setExpandedTable(null)}
              footer={
                <button
                  className="button button--ghost button--small"
                  type="button"
                  onClick={exportDrivers}
                  disabled={data.breakdown.length === 0}
                >
                  Export CSV
                </button>
              }
            >
              <DriversTable
                breakdown={data.breakdown}
                dimensionLabel={
                  dimensions.find((d) => d.value === groupBy)?.label ?? ""
                }
                currencyCode={data.currency}
                variant="full"
              />
            </TableDialog>
            <TableDialog
              open={expandedTable === "charges"}
              title="Charges — all columns"
              onClose={() => setExpandedTable(null)}
              footer={
                <button
                  className="button button--ghost button--small"
                  type="button"
                  onClick={exportCharges}
                  disabled={data.charges.length === 0}
                >
                  Export CSV
                </button>
              }
            >
              <ChargesTable
                charges={data.charges}
                currencyCode={data.currency}
                sort={sort}
                onSort={sortCharges}
                variant="full"
              />
            </TableDialog>
          </>
        )}
      </main>

      <AskMode
        active={askMode}
        onPick={pickAskTarget}
        onCancel={cancelAskMode}
      />

      <Assistant
        open={chatOpen}
        unread={unread}
        turns={turns}
        pending={chatPending}
        context={askContext}
        history={conversationSummaries}
        activeConversationId={conversationId}
        onToggle={toggleChat}
        onClearContext={() => setAskContext(null)}
        onAsk={(m) => void submitQuestion(m)}
        onNewConversation={startNewConversation}
        onOpenConversation={openConversation}
        onDeleteConversation={deleteConversation}
        onEnterAskMode={enterAskMode}
        askDisabled={!data}
      />
    </div>
  );
}

export class AppErrorBoundary extends Component<
  { children: ReactNode },
  { failed: boolean }
> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(
      JSON.stringify({
        event: "ui_error",
        errorType: error.name,
        componentStack: Boolean(info.componentStack),
      }),
    );
  }
  render() {
    if (this.state.failed)
      return (
        <div className="state-card state-card--error" role="alert">
          <div>
            <strong>Watchpoint needs to reload</strong>
            <p>No billing data was changed. Refresh the page to try again.</p>
          </div>
        </div>
      );
    return this.props.children;
  }
}
